/**
 * Upload Component - Theme-aware file upload
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 * 
 * Basic implementation - can be enhanced with drag-drop, progress, etc.
 */

const React = (globalThis as any).React;
const { useState, useRef } = React;

import { styles } from '../styles';

// ✅ Fix TypeScript error: Declare React types for type checking
type ReactNode = any;
type CSSProperties = any;
type ChangeEvent<T> = any;
type MutableRefObject<T> = { current: T };

export interface UploadFile {
  uid: string;
  name: string;
  status?: 'uploading' | 'done' | 'error';
  url?: string;
}

export interface UploadProps {
  fileList?: UploadFile[];
  beforeUpload?: (file: File) => boolean | Promise<boolean>;
  customRequest?: (options: { file: File; onSuccess: () => void; onError: (error: Error) => void }) => void;
  onChange?: (info: { fileList: UploadFile[] }) => void;
  onRemove?: (file: UploadFile) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
}

export function Upload({
  fileList: controlledFileList,
  beforeUpload,
  customRequest,
  onChange,
  onRemove,
  accept,
  multiple = false,
  disabled = false,
  children,
  style,
}: UploadProps) {
  // ✅ Fix TypeScript error: Explicitly type useState and useRef without type arguments
  const [internalFileList, setInternalFileList] = (useState as any)([]);
  const fileInputRef = (useRef as any)(null);

  const fileList = controlledFileList !== undefined ? controlledFileList : internalFileList;

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles: UploadFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check beforeUpload
      if (beforeUpload) {
        const result = await beforeUpload(file);
        if (result === false) continue;
      }

      const uploadFile: UploadFile = {
        uid: `${Date.now()}-${i}`,
        name: file.name,
        status: 'uploading',
      };

      newFiles.push(uploadFile);

      // Handle customRequest or default upload
      if (customRequest) {
        customRequest({
          file,
          onSuccess: () => {
            uploadFile.status = 'done';
            const updatedList = [...fileList, ...newFiles];
            if (controlledFileList === undefined) {
              setInternalFileList(updatedList);
            }
            onChange?.({ fileList: updatedList });
          },
          onError: (error: Error) => {
            uploadFile.status = 'error';
            const updatedList = [...fileList, ...newFiles];
            if (controlledFileList === undefined) {
              setInternalFileList(updatedList);
            }
            onChange?.({ fileList: updatedList });
          },
        });
      } else {
        // Default: mark as done immediately
        uploadFile.status = 'done';
      }
    }

    if (newFiles.length > 0) {
      const updatedList = [...fileList, ...newFiles];
      if (controlledFileList === undefined) {
        setInternalFileList(updatedList);
      }
      onChange?.({ fileList: updatedList });
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (file: UploadFile) => {
    // ✅ Fix TypeScript error: Explicitly type filter callback parameter
    const updatedList = fileList.filter((f: any) => f.uid !== file.uid);
    if (controlledFileList === undefined) {
      setInternalFileList(updatedList);
    }
    onChange?.({ fileList: updatedList });
    onRemove?.(file);
  };

  const uploadAreaStyle: CSSProperties = {
    border: '2px dashed var(--border-color)',
    borderRadius: '8px',
    padding: '24px',
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: 'var(--input-bg)',
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.5 : 1,
    ...style,
  };

  const fileItemStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    marginTop: '8px',
  };

  return React.createElement('div', {},
    React.createElement('div', {
      onClick: () => !disabled && fileInputRef.current?.click(),
      style: uploadAreaStyle,
    },
      children || React.createElement('div', {},
        React.createElement('div', { style: { fontSize: '24px', marginBottom: '8px' } }, '📁'),
        React.createElement('div', { style: { color: 'var(--main-text-secondary)' } }, 'Click or drag file to upload')
      )
    ),
    React.createElement('input', {
      ref: fileInputRef,
      type: 'file',
      accept: accept,
      multiple: multiple,
      disabled: disabled,
      onChange: handleFileChange,
      style: { display: 'none' },
    }),
    fileList.length > 0 && React.createElement('div', { style: { marginTop: '16px' } },
      // ✅ Fix TypeScript error: Explicitly type map callback parameter
      fileList.map((file: any) =>
        React.createElement('div', { key: file.uid, style: fileItemStyle },
          React.createElement('span', { style: { flex: 1 } }, file.name),
          file.status === 'uploading' && React.createElement('span', { style: { color: 'var(--main-text-secondary)', marginRight: '8px' } }, 'Uploading...'),
          file.status === 'error' && React.createElement('span', { style: { color: 'var(--error-color)', marginRight: '8px' } }, 'Error'),
          React.createElement('button', {
            onClick: () => handleRemove(file),
            style: {
              background: 'transparent',
              border: 'none',
              color: 'var(--error-color)',
              cursor: 'pointer',
              padding: '4px 8px',
            },
          }, '×')
        )
      )
    )
  );
}

// Dragger is just Upload with different styling
export function UploadDragger(props: UploadProps) {
  return React.createElement(Upload, {
    ...props,
    style: {
      ...props.style,
      border: '2px dashed var(--border-color)',
      padding: '40px',
    },
  });
}

// Attach Dragger to Upload
(Upload as any).Dragger = UploadDragger;

